import { useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'

import { useLocales } from 'src/contexts'
import {
  CURRENT_PAGE_KEY,
  ITEM_PER_PAGE,
  ITEM_PER_PAGE_GRID,
} from 'src/constants'

import { useNavigation } from 'src/hooks/useNavigation'
import { useManageMaintenanceHistory } from 'src/hooks/useManageMaintenanceHistory'
import { useInputRefForFocus } from 'src/hooks/useInputRefFocus'
import { useDebounceState } from 'src/hooks/useDebounceState'
import { useAppSelector } from 'src/hooks/useAppSelector'
import { useIsMobile } from 'src/hooks/use-mobile'
import { fillArray } from 'src/helpers/fillArray'

import {
  MaintenanceFilters,
  SortStates,
} from 'src/pages/ServiceMaintenanceHistory/types'
import {
  getCountOfActiveFilters,
  getListOfSortsStates,
  handleSortChange,
} from 'src/pages/ServiceMaintenanceHistory/helpers'
import { DEFAULT_MAINTENANCE_FILTERS } from 'src/pages/ServiceMaintenanceHistory/constants'

export const useMaintenanceHistory = () => {
  const { translations } = useLocales()
  const [searchParams, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()
  const hiddenOnMobile = isMobile ? 'hidden' : ''

  const { inputRef } = useInputRefForFocus()

  const { preferList, activeCar } = useAppSelector(state => state.user)

  const { goToMyCars } = useNavigation()

  const { allMaintenanceHistory, isLoadingGetAllMaintenanceHistory } =
    useManageMaintenanceHistory()

  const [
    searchText,
    submitSearchText,
    handleSearchChange,
    setCurrentDebouncedValue,
    setCurrentValue,
  ] = useDebounceState('')

  const [sortState, setSortState] = useState<SortStates>(SortStates.BY_NEW)
  const [isReversed, setIsReversed] = useState(false)
  const [isList, setIsList] = useState(!!preferList)

  const pageSeparator = isList ? ITEM_PER_PAGE : ITEM_PER_PAGE_GRID

  const startPage = Number(searchParams.get(CURRENT_PAGE_KEY)) || 1
  const [currentPage, setCurrentPage] = useState<number>(startPage)
  const [filters, setFilters] = useState<MaintenanceFilters>(
    DEFAULT_MAINTENANCE_FILTERS,
  )

  const filteredHistory = useMemo(() => {
    if (!allMaintenanceHistory) return []

    return allMaintenanceHistory.filter(item => {
      const matchesSearch = JSON.stringify(item)
        .toLowerCase()
        .includes(submitSearchText.toLowerCase())

      const itemOdometer = Number(item.odometer) || 0
      const itemTotalCost = Number(item.serviceCost) || 0

      const matchesOdometer =
        (filters.minOdometer === null || itemOdometer >= filters.minOdometer) &&
        (filters.maxOdometer === null || itemOdometer <= filters.maxOdometer)

      const matchServiceType =
        !filters.serviceType || item.serviceType === filters.serviceType

      const matchesTotalCost =
        (filters.minTotalCost === null ||
          itemTotalCost >= filters.minTotalCost) &&
        (filters.maxTotalCost === null || itemTotalCost <= filters.maxTotalCost)

      return (
        matchesSearch && matchesOdometer && matchServiceType && matchesTotalCost
      )
    })
  }, [allMaintenanceHistory, submitSearchText, filters])

  const sortedHistory = useMemo(() => {
    const baseArray = [...filteredHistory]

    const sortedList = baseArray.sort(handleSortChange(sortState))

    return isReversed ? [...sortedList].reverse() : sortedList
  }, [filteredHistory, sortState, isReversed])

  const listOfSorts = useMemo(() => {
    return getListOfSortsStates(translations.maintenance.historyMaintenance)
  }, [translations])

  const handleChangeSortState = (value: SortStates) => setSortState(value)

  const toggleReversed = (): void => {
    setIsReversed(prevState => !prevState)
  }

  const paginatedHistory = useMemo(() => {
    const start = (currentPage - 1) * pageSeparator

    return sortedHistory.slice(start, start + pageSeparator)
  }, [sortedHistory, currentPage, isList])

  const totalPages = useMemo(
    () => Math.ceil(filteredHistory.length / pageSeparator),
    [filteredHistory, isList],
  )

  const handleNextPage = (): void => {
    setCurrentPage(prevState => {
      if (prevState >= totalPages) return prevState

      const newPage = prevState + 1
      setSearchParams({ [CURRENT_PAGE_KEY]: newPage.toString() })

      return newPage
    })
  }

  const handlePreviousPage = (): void => {
    setCurrentPage(prevState => {
      if (prevState <= 1) return prevState

      const newPage = prevState - 1
      setSearchParams({ [CURRENT_PAGE_KEY]: newPage.toString() })

      return newPage
    })
  }

  const handleSetPage = (page: number): void => {
    setSearchParams({ [CURRENT_PAGE_KEY]: page.toString() })
    setCurrentPage(page)
  }

  const toggleListDisplay = (): void => {
    if (isMobile) return

    setIsList(prevIsList => {
      const newIsList = !prevIsList
      const newPageSeparator = newIsList ? ITEM_PER_PAGE : ITEM_PER_PAGE_GRID
      const newTotalPages = Math.max(
        1,
        Math.ceil(filteredHistory.length / newPageSeparator),
      )

      setCurrentPage(prevPage => {
        const newPage = Math.min(prevPage, newTotalPages)

        setSearchParams({ [CURRENT_PAGE_KEY]: newPage.toString() })

        return newPage
      })

      return newIsList
    })
  }

  const clearInput = (): void => {
    setCurrentDebouncedValue('')
    setCurrentValue('')
  }

  useEffect(() => {
    if (!isMobile) return

    setIsList(true)
  }, [isMobile, isList])

  const breadcrumbItems = [
    { labelKey: translations.breadcrumbItems.home, href: '/' },
    {
      labelKey: translations.breadcrumbItems.serviceMaintenance,
      href: '/service',
    },
    { labelKey: translations.breadcrumbItems.history },
  ]

  return {
    locale: translations.maintenance.historyMaintenance,
    breadcrumbItems,
    isLoadingGetAllMaintenanceHistory,
    allMaintenanceHistory: paginatedHistory,
    listOfSorts,
    handleSearchChange,
    searchText,
    sortState,
    handleChangeSortState,
    isReversed,
    toggleReversed,
    handleNextPage,
    handlePreviousPage,
    handleSetPage,
    currentPage,
    arrPages: fillArray(totalPages),
    isList,
    toggleListDisplay,
    clearInput,
    filters,
    setFilters,
    filtersCount: getCountOfActiveFilters(filters),
    inputRef,
    currentCar: translations.currentCar,
    goToMyCars,
    activeCar,
    numberOfFoundItems: sortedHistory.length,
    hiddenOnMobile,
  }
}
